/**
 * Fixture adapter for development/preview composition.
 *
 * Figma authority: R4VYIdXEv7HYIXtpDMEvHY, node 38:2510
 *
 * CORRECTION02 isolation contract:
 *   Every invocation of createFixtureCommentsAdapter() returns an independent
 *   CommentsDependencies with its own private mutable state:
 *     - submittedComments: Comment[]    (per-instance submission store)
 *     - commentLikeState:  Map<string, {liked, count}>  (per-instance like state)
 *     - commentIdCounter:  number      (per-instance ID generator)
 *     - signalLatches:     WeakSet<AbortSignal>  (per-instance abort dedupe)
 *
 *   Two factory calls MUST NOT observe each other's mutations. Each adapter
 *   instance starts from the canonical Figma fixture snapshot and diverges
 *   independently thereafter.
 *
 *   Static immutable seed objects (FIXTURE_ROOT_POST, FIXTURE_COMMENTS,
 *   FIXTURE_CURRENT_USER, KNOWN_POST_IDS) may remain module-level because
 *   callers cannot mutate them.
 *
 *   This module intentionally does NOT export formatRelativeTime — the
 *   formatter lives in ./formatRelativeTime so production code can import it
 *   without coupling to fixture data.
 */
import type { AuthorSummary, Comment, RootPost } from '../model';
import type {
  CommentsDependencies,
  LoadCommentsResult,
  SubmitCommentResult,
  ToggleLikeResult,
} from '../effects';
import { buildAuthorSummary } from '../model';

// =============================================================================
// Fixture data — module-level immutable seeds
// =============================================================================

/**
 * Fixed reference time used by submitComment for createdAt stamping.
 * Tests that need a deterministic reference time should also use this
 * (the formatter accepts a Date argument so test code is not coupled to it).
 */
export const FIXTURE_REFERENCE_TIME = new Date('2024-01-15T12:00:00Z');

/**
 * Fixture root post matching Figma design.
 * Анна • 27, 2 часа назад
 */
export const FIXTURE_ROOT_POST: RootPost = {
  id: 'post-anna-001',
  author: buildAuthorSummary({
    displayName: 'Анна',
    age: 27,
    isAnonymous: false,
  }),
  body: 'Сегодня поняла, что хорошие разговоры ценнее красивых фотографий.',
  createdAt: '2024-01-15T10:00:00Z',
};

/**
 * Fixture comments matching Figma design.
 *
 * Each instance of createFixtureCommentsAdapter() copies this seed into its
 * own private like-state map so toggles never leak across instances.
 */
export const FIXTURE_COMMENTS: readonly Comment[] = [
  {
    id: 'comment-mikhail-001',
    author: buildAuthorSummary({
      displayName: 'Михаил',
      age: 32,
      isAnonymous: false,
    }),
    body: 'Полностью согласен. Последний раз по-настоящему хорошо поговорил три месяца назад.',
    createdAt: '2024-01-15T11:00:00Z',
    likeCount: 5,
    likedByCurrentUser: false,
  },
  {
    id: 'comment-olga-001',
    author: buildAuthorSummary({
      displayName: 'Ольга',
      age: 29,
      isAnonymous: false,
    }),
    body: 'Это точно! Умение слушать — редкость в наше время.',
    createdAt: '2024-01-15T10:30:00Z',
    likeCount: 8,
    likedByCurrentUser: false,
  },
  {
    id: 'comment-anon-001',
    author: buildAuthorSummary({
      displayName: 'Аноним',
      age: null,
      isAnonymous: true,
    }),
    body: 'А у меня наоборот — сначала разговор, потом всё остальное. Работает лучше.',
    createdAt: '2024-01-15T09:30:00Z',
    likeCount: 3,
    likedByCurrentUser: false,
  },
];

/**
 * Fixture current user for composer avatar.
 */
export const FIXTURE_CURRENT_USER: AuthorSummary = buildAuthorSummary({
  displayName: 'Текущий',
  age: 30,
  isAnonymous: false,
});

/**
 * Known post IDs that return fixture data.
 * Unknown IDs return not_found.
 *
 * Module-level because it is read-only and intentionally shared across all
 * adapter instances (matches production "known posts" semantics).
 */
const KNOWN_POST_IDS = new Set(['post-anna-001']);

// =============================================================================
// Factory — per-instance closure state
// =============================================================================

/**
 * Create the production-neutral fixture adapter.
 *
 * Each call returns a NEW CommentsDependencies whose loadComments / submitComment
 * / toggleLike / loadCurrentUser methods read and write PRIVATE state captured
 * in this factory's closure. State NEVER crosses between instances.
 *
 * Per-instance state:
 *   - submittedComments   Comment[]                (submissions accumulated here)
 *   - commentLikeState    Map<id, {liked, count}>  (per-comment like state)
 *   - commentIdCounter    number                   (monotonic comment id source)
 *   - abortedSignals      WeakSet<AbortSignal>     (idempotent abort dedupe)
 */
export function createFixtureCommentsAdapter(): CommentsDependencies {
  // ---- Private state captured by closure ------------------------------
  const submittedComments: Comment[] = [];
  const commentLikeState: Map<string, { liked: boolean; count: number }> = new Map(
    FIXTURE_COMMENTS.map((c) => [c.id, { liked: c.likedByCurrentUser, count: c.likeCount }])
  );
  let commentIdCounter = 1000;
  const abortedSignals: WeakSet<AbortSignal> = new WeakSet();

  // ---- Helpers -------------------------------------------------------
  function isAbortFresh(signal: AbortSignal): boolean {
    if (signal.aborted) {
      abortedSignals.add(signal);
      return false;
    }
    return true;
  }

  function snapshotComments(): readonly Comment[] {
    // Combine canonical fixture comments with this instance's submissions.
    // Both arrays are immutable references — fixture data is not mutated,
    // only like-state and submission accumulation live in closure state.
    return [...FIXTURE_COMMENTS, ...submittedComments];
  }

  // ---- Adapter object ------------------------------------------------
  return {
    loadComments: {
      async load(postId: string, signal: AbortSignal): Promise<LoadCommentsResult> {
        if (!isAbortFresh(signal)) {
          return Promise.reject(new DOMException('Aborted', 'AbortError'));
        }

        // Simulate network delay (long enough that route replacements
        // can race this promise cleanly).
        await new Promise((r) => setTimeout(r, 100));

        if (!isAbortFresh(signal)) {
          return Promise.reject(new DOMException('Aborted', 'AbortError'));
        }

        if (!KNOWN_POST_IDS.has(postId)) {
          return { kind: 'not_found' };
        }

        return {
          kind: 'found',
          post: FIXTURE_ROOT_POST,
          comments: snapshotComments(),
        };
      },
    },

    submitComment: {
      async submit(
        _postId: string,
        content: string,
        signal: AbortSignal
      ): Promise<SubmitCommentResult> {
        if (!isAbortFresh(signal)) {
          return Promise.reject(new DOMException('Aborted', 'AbortError'));
        }

        await new Promise((r) => setTimeout(r, 150));

        if (!isAbortFresh(signal)) {
          return Promise.reject(new DOMException('Aborted', 'AbortError'));
        }

        commentIdCounter += 1;
        const newComment: Comment = {
          id: `comment-fixture-${commentIdCounter}`,
          author: FIXTURE_CURRENT_USER,
          body: content,
          createdAt: FIXTURE_REFERENCE_TIME.toISOString(),
          likeCount: 0,
          likedByCurrentUser: false,
        };

        submittedComments.push(newComment);
        return { kind: 'created', comment: newComment };
      },
    },

    toggleLike: {
      async toggle(commentId: string, signal: AbortSignal): Promise<ToggleLikeResult> {
        if (!isAbortFresh(signal)) {
          return Promise.reject(new DOMException('Aborted', 'AbortError'));
        }

        await new Promise((r) => setTimeout(r, 50));

        if (!isAbortFresh(signal)) {
          return Promise.reject(new DOMException('Aborted', 'AbortError'));
        }

        const state = commentLikeState.get(commentId);
        if (!state) {
          // Comment ID unknown to THIS instance's like-state map.
          // Could be a stale ID from a different factory instance, or a
          // submission that was rolled back. Return unavailable so the
          // caller rolls back the optimistic update.
          return { kind: 'unavailable' };
        }

        const newLiked = !state.liked;
        const newCount = state.liked ? state.count - 1 : state.count + 1;
        commentLikeState.set(commentId, { liked: newLiked, count: newCount });

        return { kind: 'success', liked: newLiked, count: newCount };
      },
    },

    loadCurrentUser: {
      async load(signal: AbortSignal): Promise<AuthorSummary | null> {
        if (!isAbortFresh(signal)) {
          return Promise.reject(new DOMException('Aborted', 'AbortError'));
        }

        await new Promise((r) => setTimeout(r, 50));

        if (!isAbortFresh(signal)) {
          return Promise.reject(new DOMException('Aborted', 'AbortError'));
        }

        return FIXTURE_CURRENT_USER;
      },
    },
  };
}